import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import ChatWidget from '../ChatWidget';
import {
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export default function AppLayout() {
  const { user, logout, isImpersonating, stopImpersonating } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-400 text-amber-900 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-3">
          <span>Viewing as <strong>{user?.business_name}</strong></span>
          <button
            onClick={stopImpersonating}
            className="inline-flex items-center gap-1 px-3 py-1 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors"
          >
            <XMarkIcon className="h-3 w-3" />
            Exit
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-primary-600 text-white rounded-lg px-3 py-2 font-bold">T</div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Trailhead</h1>
                <p className="text-xs text-gray-600">{user?.business_name}</p>
              </div>
            </Link>

            <div className="flex items-center space-x-4">
              {user?.is_admin && !isImpersonating && (
                <Link
                  to="/admin"
                  className="flex items-center space-x-1 text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-700">
                <UserCircleIcon className="h-5 w-5" />
                <span>{user?.email}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-1">
          <p className="text-center text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Happy Camper Productions. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
            <a href="https://happycamperproductions.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 underline">Privacy Policy</a>
            <span className="text-gray-300">|</span>
            <a href="https://happycamperproductions.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 underline">Terms of Service</a>
          </div>
        </div>
      </footer>

      {/* Chat Widget — only show for non-admin or impersonating */}
      {(!user?.is_admin || isImpersonating) && <ChatWidget />}
    </div>
  );
}
